import { Banner, Box, Text } from '@betterment/samba-react';
import { SambaAutoLink } from '@betterment/shared-components';

import { CloseAccountModal } from './close-account/CloseAccountModal';
import { PurposeSection } from './PurposeSection';

import { NarrowTypename } from '../../../../graphql/utils/narrow-typename';
import { ModalSection } from '../../../../Shared/components-v2/modal-section/ModalSection';
import { ModalSectionRow } from '../../../../Shared/components-v2/modal-section/ModalSectionRow';
import { urlBuilder } from '../../../../Shared/urlBuilder';
import { EnvelopeAccountSettingsModalQuery } from '../graphql/accountSettingsModal.generated';
import { formatLegalAccountType, formatTCPAccounts } from '../helper';

type ManagedAccount = NarrowTypename<EnvelopeAccountSettingsModalQuery['envelopeAccount'], 'ManagedAccount'>;

type ManagedAccountSettingsProps = {
  envelopeAccount: ManagedAccount;
};

export const ManagedAccountSettings: React.FC<ManagedAccountSettingsProps> = ({ envelopeAccount }) => {
  const { isTaxCoordinated, legacyGoalId, portfolio, subAccounts, autoglide } = envelopeAccount;
  const legalAccountType = formatLegalAccountType(subAccounts[0].legalAccount);
  const tcpAccounts = formatTCPAccounts(subAccounts);
  const currentPath = window.location.pathname;
  const hasBeneficiaries = subAccounts.some(subAccount => subAccount.legalAccount.primaryBeneficiaries.length > 0);

  const editStrategyUrl = urlBuilder.appGoalsEditPortfolioStrategy.absolute({
    goalId: legacyGoalId,
    return_to: currentPath,
  });
  const editAllocationUrl = urlBuilder.appGoalsEditAllocation.absolute({
    goal_id: legacyGoalId,
    return_to: currentPath,
  });
  const editExternallyAdvisedPlanUrl = urlBuilder.appGoalsEditAllocation.absolute({
    goal_id: legacyGoalId,
    return_to: currentPath,
  });
  const editAutoAdjustUrl = urlBuilder.appGoalsEditAllocation.absolute({
    goal_id: legacyGoalId,
    return_to: currentPath,
  });

  const portfolioDetails = (() => {
    switch (portfolio?.__typename) {
      case 'RiskLevelBasedPortfolio': {
        const { strategy, riskLevel, isStrategySelectionEligible, isAllocationEditable } = portfolio;
        return {
          strategy: isStrategySelectionEligible
            ? {
                name: strategy.name,
                editUrl: editStrategyUrl,
              }
            : null,
          allocation: isAllocationEditable
            ? {
                description: riskLevel.name,
                editUrl: urlBuilder.appGoalsEditAllocation.absolute({ goal_id: legacyGoalId, return_to: currentPath }),
              }
            : null,
        };
      }
      case 'SelfDirectedPortfolio': {
        const { isExternallyAdvised, isStrategySelectionEligible, isAllocationEditable, targetAllocationDescription } =
          portfolio;
        return {
          strategy:
            isStrategySelectionEligible && !isExternallyAdvised
              ? {
                  name: portfolio.name,
                  editUrl: editStrategyUrl,
                }
              : null,
          allocation: isAllocationEditable
            ? {
                description: targetAllocationDescription,
                editUrl: isExternallyAdvised ? editExternallyAdvisedPlanUrl : editAllocationUrl,
              }
            : null,
        };
      }
      default:
        return null;
    }
  })();

  return (
    <Box css={{ display: 'grid', gap: 'space.200' }}>
      <ModalSection header="Account details">
        <ModalSectionRow>
          <ModalSectionRow.LeadingContent>
            <Text>Account type</Text>
          </ModalSectionRow.LeadingContent>
          <ModalSectionRow.TrailingContent>
            <Text>{isTaxCoordinated ? 'Multiple accounts' : legalAccountType}</Text>
          </ModalSectionRow.TrailingContent>
        </ModalSectionRow>
        {!hasBeneficiaries && (
          <ModalSectionRow>
            <ModalSectionRow.LeadingContent>
              <Text>Beneficiaries</Text>
            </ModalSectionRow.LeadingContent>
            <ModalSectionRow.TrailingContent>
              <SambaAutoLink to={urlBuilder.getLegalAccounts.absolute()}>Add beneficiary</SambaAutoLink>
            </ModalSectionRow.TrailingContent>
          </ModalSectionRow>
        )}
        {isTaxCoordinated && (
          <Banner variant="info" css={{ my: 'space.100' }}>
            <Banner.Content>
              <Text color="text.weak">
                Your accounts are organized into a tax coordinated portfolio that includes {tcpAccounts} accounts.
              </Text>
            </Banner.Content>
          </Banner>
        )}
      </ModalSection>
      <ModalSection header="Portfolio">
        {portfolioDetails?.strategy && (
          <ModalSectionRow>
            <ModalSectionRow.LeadingContent>
              <Text>Portfolio strategy</Text>
            </ModalSectionRow.LeadingContent>
            <ModalSectionRow.TrailingContent>
              <Text>{portfolioDetails.strategy.name}</Text>
              <SambaAutoLink to={portfolioDetails.strategy.editUrl}>Edit</SambaAutoLink>
            </ModalSectionRow.TrailingContent>
          </ModalSectionRow>
        )}
        {portfolioDetails?.allocation && (
          <ModalSectionRow>
            <ModalSectionRow.LeadingContent>
              <Text>Allocation</Text>
            </ModalSectionRow.LeadingContent>
            <ModalSectionRow.TrailingContent>
              <Text>{portfolioDetails.allocation.description}</Text>
              <SambaAutoLink to={portfolioDetails.allocation.editUrl}>Edit</SambaAutoLink>
            </ModalSectionRow.TrailingContent>
          </ModalSectionRow>
        )}
        {autoglide.isEligible && (
          <ModalSectionRow>
            <ModalSectionRow.LeadingContent>
              <Text>Auto-adjust</Text>
            </ModalSectionRow.LeadingContent>
            <ModalSectionRow.TrailingContent>
              <Text data-testid="auto-adjust-value">{autoglide.isEnabled ? 'On' : 'Off'}</Text>
              <SambaAutoLink to={editAutoAdjustUrl}>Edit</SambaAutoLink>
            </ModalSectionRow.TrailingContent>
          </ModalSectionRow>
        )}
        {envelopeAccount.isTaxCoordinationEligible && (
          <ModalSectionRow>
            <ModalSectionRow.LeadingContent>
              <Text>Tax Coordination</Text>
            </ModalSectionRow.LeadingContent>
            <ModalSectionRow.TrailingContent>
              <Text data-testid="tax-coordination-value">{isTaxCoordinated ? 'On' : 'Off'}</Text>
              {isTaxCoordinated ? (
                <SambaAutoLink
                  to={urlBuilder.appGoalTaxLocationChangeNew.absolute({
                    goalId: legacyGoalId,
                    return_to: currentPath,
                  })}
                >
                  Edit
                </SambaAutoLink>
              ) : (
                <SambaAutoLink to={urlBuilder.getTaxLocationSetup.absolute()}>Edit</SambaAutoLink>
              )}
            </ModalSectionRow.TrailingContent>
          </ModalSectionRow>
        )}
      </ModalSection>
      <PurposeSection envelopeAccount={envelopeAccount} />
      <CloseAccountModal
        balance={envelopeAccount.balance}
        purposeName={envelopeAccount.envelope.purpose?.name}
        name={envelopeAccount.name}
        legacyAccountId={envelopeAccount.subAccounts[0].legacySubAccountId}
        triggerText="Close account"
      />
    </Box>
  );
};
